import { httpsCallable } from "firebase/functions";
import { doc, getDoc, increment, serverTimestamp, updateDoc } from "firebase/firestore";

import { auth, db, functions } from "../firebase";
import { pipStorageKey, isPipOnCooldown, PIP_COOLDOWN_MS } from "../constants/pipConfig";

export const FAN_ONBOARDING_BONUS = 50;
export const PAGE_TIP_FLAGS = ["seenFollowTip", "seenCreditsTip", "seenCollectionTip"];

const awardPipCreditsFn = httpsCallable(functions, "awardPipCredits");
const buyPipWardrobeItemFn = httpsCallable(functions, "buyPipWardrobeItem");
const equipPipOutfitFn = httpsCallable(functions, "equipPipOutfit");
const markPipIntroSeenFn = httpsCallable(functions, "markPipIntroSeen");

function callableError(err) {
  return err?.message || err?.details || "Request failed.";
}

export function getLocalPipLastFound() {
  return parseInt(localStorage.getItem(pipStorageKey()) || "0", 10);
}

export function setLocalPipLastFound(ts = Date.now()) {
  localStorage.setItem(pipStorageKey(), String(ts));
}

export function checkPipCooldown(userPerks) {
  const local = getLocalPipLastFound();
  const remote = userPerks?.pipLastFound?.seconds
    ? userPerks.pipLastFound.seconds * 1000
    : 0;
  const last = Math.max(local, remote);
  return {
    onCooldown: isPipOnCooldown(last),
    lastFoundMs: last,
    remainingMs: Math.max(PIP_COOLDOWN_MS - (Date.now() - last), 0),
  };
}

export async function awardPipFind(categoryId) {
  try {
    const { data } = await awardPipCreditsFn({ categoryId });
    setLocalPipLastFound();
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function buyPipItem(itemId) {
  try {
    const { data } = await buyPipWardrobeItemFn({ itemId });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function equipPipItem(slot, itemId) {
  try {
    const { data } = await equipPipOutfitFn({ slot, itemId });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function dismissPipIntro() {
  return completeFanOnboarding();
}

export async function completeFanOnboarding(userPerks) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in required.");

  let creditsAwarded = 0;
  try {
    const { data } = await markPipIntroSeenFn();
    creditsAwarded = Number(data?.creditsAwarded) || 0;
  } catch (err) {
    console.warn("markPipIntroSeen:", err?.message || err);
  }

  const freshSnap = await getDoc(doc(db, "userPerks", uid));
  const fresh = freshSnap.exists() ? freshSnap.data() : {};
  const alreadyAwarded = Boolean(
    userPerks?.fanOnboardingCreditAwarded || fresh.fanOnboardingCreditAwarded
  );

  const patch = {
    fanOnboardingComplete: true,
    pipIntroSeen: true,
    fanOnboardingCreditAwarded: true,
    updatedAt: serverTimestamp(),
  };

  if (!alreadyAwarded && creditsAwarded <= 0) {
    patch.credits = increment(FAN_ONBOARDING_BONUS);
    patch.totalCreditsEarned = increment(FAN_ONBOARDING_BONUS);
    creditsAwarded = FAN_ONBOARDING_BONUS;
  } else if (alreadyAwarded && creditsAwarded <= 0) {
    creditsAwarded = 0;
  }

  await updateDoc(doc(db, "userPerks", uid), patch);
  return { success: true, creditsAwarded };
}

export async function markPageTipSeen(flag) {
  const uid = auth.currentUser?.uid;
  if (!uid || !PAGE_TIP_FLAGS.includes(flag)) return;
  await updateDoc(doc(db, "userPerks", uid), {
    [flag]: true,
    updatedAt: serverTimestamp(),
  });
}

export async function resetPageTips() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in required.");
  await updateDoc(doc(db, "userPerks", uid), {
    seenFollowTip: false,
    seenCreditsTip: false,
    seenCollectionTip: false,
    updatedAt: serverTimestamp(),
  });
}

export async function fetchPipMasters() {
  const snap = await getDoc(doc(db, "platform", "pipMasters"));
  if (!snap.exists()) return { currentMaster: null, allMasters: [] };
  return snap.data();
}

export function markPageVisited(pathname) {
  try {
    const key = "pipVisitedPages";
    const visited = JSON.parse(localStorage.getItem(key) || "[]");
    if (!visited.includes(pathname)) {
      visited.push(pathname);
      localStorage.setItem(key, JSON.stringify(visited));
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function hasVisitedPage(pathname) {
  try {
    const visited = JSON.parse(localStorage.getItem("pipVisitedPages") || "[]");
    return visited.includes(pathname);
  } catch {
    return false;
  }
}

export const PIP_EVENT = "pip-trigger-event";

export function dispatchPipEvent(type, detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PIP_EVENT, { detail: { type, ...detail } }));
}

export function getInactiveDays(lastActiveDate) {
  if (!lastActiveDate) return 0;
  const last = typeof lastActiveDate === "string"
    ? new Date(lastActiveDate)
    : new Date(lastActiveDate);
  return Math.floor((Date.now() - last.getTime()) / (1000 * 60 * 60 * 24));
}
